// redex/tasks/taskActions.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import { useNavigate } from "react-router-dom";
import {
  tasksListApi,
  createTasksApi,
  projectMenuApi,
  clientMenuApi,
  mainMenuApi,
  submenu1Api,
  submenu2Api,
  submenu3Api,
  submenu4Api,
  tasksAssignApi,
  updateDevelopmentTasksApi,
  getTaskAssignmentApi,
  createStatusTrackerApi,
  getTaskDevelopmentApi,
  raisedbyDetailsApi,
  lastEndTimesApi,
  supportTeamTaskCreateApi,
  supportTeamTaskListApi,
  lastSyncDateTimeApi,
  historyListAPI,
  queryFromApi,
} from "./taskApi";
import { showApiErrorToast } from "../../utils/toastErrorHandler";

/* End the session on 401, and only on 401.
 *
 * Every thunk below used to drop the token and the user on ANY failure. A 500
 * from one endpoint, a validation refusal, a laptop that missed a single
 * request — each one quietly signed the whole app out: the header lost its
 * user, the clock button went dead, and the next screen to read the missing
 * key crashed on it. That is how completing a task ended in a red overlay.
 *
 * A request that failed is not a token that expired, and only the second is a
 * reason to throw somebody out of the app. */
const endSessionIfUnauthorized = (err) => {
  if (err?.response?.status !== 401) return false;
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  return true;
};

// Thunk for Creating Data
export const createTasksApis = createAsyncThunk(
  "tasks/create", // Unique string identifier
  async (payload, { rejectWithValue }) => {
    try {
      return await createTasksApi(payload);
    } catch (err) {
      // Return the specific error message from the API
      return rejectWithValue(err?.message || "Failed to create task");
    }
  },
);

export const supportTeamTaskCreateApis = createAsyncThunk(
  "supportTeamTask/create", // Unique string identifier
  async (payload, { rejectWithValue }) => {
    try {
      return await supportTeamTaskCreateApi(payload);
    } catch (err) {
      // Return the specific error message from the API
      return rejectWithValue(err?.message || "Failed to create task");
    }
  },
);

// Thunk for Creating Data
export const tasksAssignApis = createAsyncThunk(
  "tasksAssign/create", // Unique string identifier
  async (payload, { rejectWithValue }) => {
    try {
      return await tasksAssignApi(payload);
    } catch (err) {
      // Return the specific error message from the API
      return rejectWithValue(err?.message || "Failed to create task");
    }
  },
);

// redux/tasks/taskActions.js
export const tasksApis = createAsyncThunk(
  "tasks/fetchList",
  async ({ payload, navigate }, { rejectWithValue }) => {
    try {
      return await tasksListApi(payload);
    } catch (err) {
      /* Only a dead session sends anybody back to the login screen. A list
         that failed to load leaves them where they are, with the reason. */
      if (endSessionIfUnauthorized(err)) {
        setTimeout(() => {
          navigate("/login", { replace: true });
        }, 1500);
      }

      showApiErrorToast(err?.response?.data?.error || err.message);

      return rejectWithValue(err?.response?.data?.message || err.message);
    }
  },
);

export const projectMenuApis = createAsyncThunk(
  "projectMenu/fetchList", // Unique string identifier
  async (payload, { rejectWithValue }) => {
    try {
      return await projectMenuApi(payload);
    } catch (err) {
      endSessionIfUnauthorized(err);

      showApiErrorToast(err?.response?.data?.error || err.message);

      return rejectWithValue(err?.response?.data?.message || err.message);
    }
  },
);

export const clientMenuApis = createAsyncThunk(
  "clientMenu/fetchList",
  async (payload, { rejectWithValue }) => {
    try {
      return await clientMenuApi(payload);
    } catch (err) {
      endSessionIfUnauthorized(err);

      showApiErrorToast(err?.response?.data?.error || err.message);

      return rejectWithValue(err?.response?.data?.message || err.message);
    }
  },
);

export const mainMenuApis = createAsyncThunk(
  "mainMenuApi/fetchList",
  async (payload, { rejectWithValue }) => {
    try {
      return await mainMenuApi(payload);
    } catch (err) {
      endSessionIfUnauthorized(err);

      showApiErrorToast(err?.response?.data?.error || err.message);

      return rejectWithValue(err?.response?.data?.message || err.message);
    }
  },
);

export const submenu1Apis = createAsyncThunk(
  "submenu1Api/fetchList",
  async (payload, { rejectWithValue }) => {
    try {
      return await submenu1Api(payload);
    } catch (err) {
      endSessionIfUnauthorized(err);

      showApiErrorToast(err?.response?.data?.error || err.message);

      return rejectWithValue(err?.response?.data?.message || err.message);
    }
  },
);

export const submenu2Apis = createAsyncThunk(
  "submenu2Api/fetchList",
  async (payload, { rejectWithValue }) => {
    try {
      return await submenu2Api(payload);
    } catch (err) {
      endSessionIfUnauthorized(err);

      showApiErrorToast(err?.response?.data?.error || err.message);

      return rejectWithValue(err?.response?.data?.message || err.message);
    }
  },
);

export const submenu3Apis = createAsyncThunk(
  "submenu3Api/fetchList",
  async (payload, { rejectWithValue }) => {
    try {
      return await submenu3Api(payload);
    } catch (err) {
      endSessionIfUnauthorized(err);

      showApiErrorToast(err?.response?.data?.error || err.message);

      return rejectWithValue(err?.response?.data?.message || err.message);
    }
  },
);

export const submenu4Apis = createAsyncThunk(
  "submenu4Api/fetchList",
  async (payload, { rejectWithValue }) => {
    try {
      return await submenu4Api(payload);
    } catch (err) {
      endSessionIfUnauthorized(err);

      showApiErrorToast(err?.response?.data?.error || err.message);

      return rejectWithValue(err?.response?.data?.message || err.message);
    }
  },
);

// Thunk for Updating Data
export const updateDevelopmentTasksApis = createAsyncThunk(
  "tasks/update",
  async (payload, { rejectWithValue }) => {
    try {
      return await updateDevelopmentTasksApi(payload);
    } catch (err) {
      // localStorage.removeItem("token");
      // localStorage.removeItem("user");

      /* Hand showApiErrorToast the whole error, not a pre-picked field. The
         API answers refusals as { success:false, message }, and reading
         `data.error` first meant the only branch that ever matched was the
         axios fallback — so "You already have a task currently 'In
         Progress'..." reached the user as "Request failed with status code
         500". showApiErrorToast already knows the shapes; let it choose. */
      showApiErrorToast(err);

      return rejectWithValue(err?.response?.data?.message || err.message);
    }
  },
);

export const createStatusTrackerApis = createAsyncThunk(
  "tasks/create-status-tracker",
  async (payload, { rejectWithValue }) => {
    try {
      return await createStatusTrackerApi(payload);
    } catch (err) {
      endSessionIfUnauthorized(err);

      showApiErrorToast(err?.response?.data?.error || err.message);

      return rejectWithValue(err?.response?.data?.message || err.message);
    }
  },
);

export const getTaskAssignmentApis = createAsyncThunk(
  "taskAssignmentApi/taskAssignmentList",
  async (apiParams, { rejectWithValue }) => {
    try {
      // apiParams here is just: { isManuallyRaised, currentPage, pageSize, edit }
      return await getTaskAssignmentApi(apiParams);
    } catch (err) {
      endSessionIfUnauthorized(err);

      showApiErrorToast(err?.response?.data?.error || err.message);

      return rejectWithValue(err?.response?.data?.message || err.message);
    }
  },
);

export const getTaskDevelopmentApis = createAsyncThunk(
  "taskDevelopmentApi/taskDevelopmentList",
  async (apiParams, { rejectWithValue }) => {
    try {
      // apiParams here is just: { isManuallyRaised, currentPage, pageSize, edit }
      return await getTaskDevelopmentApi(apiParams);
    } catch (err) {
      endSessionIfUnauthorized(err);

      showApiErrorToast(err?.response?.data?.error || err.message);

      return rejectWithValue(err?.response?.data?.message || err.message);
    }
  },
);

export const raisedbyDetailsApis = createAsyncThunk(
  "raisedbyDetails/fetchList",
  async (apiParams, { rejectWithValue }) => {
    try {
      // apiParams here is just: { isManuallyRaised, currentPage, pageSize, edit }
      return await raisedbyDetailsApi(apiParams);
    } catch (err) {
      endSessionIfUnauthorized(err);

      showApiErrorToast(err?.response?.data?.error || err.message);

      return rejectWithValue(err?.response?.data?.message || err.message);
    }
  },
);

export const lastEndTimeApis = createAsyncThunk(
  "lastEndTime/fetchList",
  async (apiParams, { rejectWithValue }) => {
    try {
      // apiParams here is just: { isManuallyRaised, currentPage, pageSize, edit }
      return await lastEndTimesApi(apiParams);
    } catch (err) {
      endSessionIfUnauthorized(err);

      showApiErrorToast(err?.response?.data?.error || err.message);

      return rejectWithValue(err?.response?.data?.message || err.message);
    }
  },
);


export const supportTeamTaskListApis = createAsyncThunk(
  "supportTeamTaskList/supportTeamTaskList",
  async (apiParams, { rejectWithValue }) => {
    try {
      // apiParams here is just: { isManuallyRaised, currentPage, pageSize, edit }
      return await supportTeamTaskListApi(apiParams);
    } catch (err) {
      endSessionIfUnauthorized(err);

      showApiErrorToast(err?.response?.data?.error || err.message);

      return rejectWithValue(err?.response?.data?.message || err.message);
    }
  },
);


export const historyListAPIs = createAsyncThunk(
  "historyList/historyList",
  async (apiParams, { rejectWithValue }) => {
    try {
      // apiParams here is just: { isManuallyRaised, currentPage, pageSize, edit }
      return await historyListAPI(apiParams);
    } catch (err) {
      endSessionIfUnauthorized(err);

      showApiErrorToast(err?.response?.data?.error || err.message);

      return rejectWithValue(err?.response?.data?.message || err.message);
    }
  },
);

export const queryFromApis = createAsyncThunk(
  "queryFrom/fetchList",
  async (payload, { rejectWithValue }) => {
    try {
      return await queryFromApi(payload);
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message);
    }
  },
);
